import { useEffect, useRef, useState, type JSX } from "react";
import type { MergeLeadsResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Reason } from "../../design/components/Reason";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { duplicatePairQuery } from "./cleanup-queries";
import type { CleanupOutcome } from "./DataCleanupDialog";
import { GroupView, usePairActions } from "./DuplicatesTab";
import "./cleanup.css";

// «Unisci» dalla scheda del Lead (LEAD-28): la finestra di «Pulizia dati» con la sola coppia del Lead e dell'altro, la stessa tabella
// campo per campo (`GroupView`) e le stesse azioni (`usePairActions`). La coppia si rilegge a ogni apertura, con le versioni di
// adesso. Un rifiuto (un Lead cambiato nel frattempo, un valore che la scheda non accetta) si legge nel piede della finestra (UX-7),
// che resta aperta; un'unione o «Non sono doppioni» riusciti chiudono la finestra, e la scheda dice l'esito (`onMerged`,
// `onDismissed`). Una coppia che non c'è più (unita o segnata da un collega, un numero cambiato) lo dice, e la scheda si rilegge
// (`onGone`).

/** Il rifiuto della coppia che vuol dire «non c'è più»: i due Lead non sono più una coppia, oppure uno non si vede più. */
function pairGone(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  return error.code === "lead_not_found" || (error.code === "merge_invalid" && error.details?.reason === "not_duplicates");
}

type Props = {
  tab: TabContext;
  view: string | null;
  timeZone: string;
  canChangeOwner: boolean;
  /** Il Lead della scheda e l'altro della coppia. */
  leadId: string;
  otherLeadId: string;
  onClose: () => void;
  onMerged: (response: MergeLeadsResponse, text: string) => void;
  onDismissed: (text: string) => void;
  onGone: () => void;
};

export function MergePairDialog({ tab, view, timeZone, canChangeOwner, leadId, otherLeadId, onClose, onMerged, onDismissed, onGone }: Props): JSX.Element {
  const t = useT();
  const pair = useQuery(duplicatePairQuery(tab, view, leadId, otherLeadId));
  const [problem, setProblem] = useState<string | null>(null);
  const success = useRef<string | null>(null);
  // Mentre un'azione è in corso la coppia si rilegge e, riuscita l'unione, non c'è più: non è una coppia sparita da dire.
  const [acting, setActing] = useState(false);
  const problemRef = useRef<HTMLParagraphElement>(null);
  const { busy, ctx, mergeGroup, dismissGroup } = usePairActions(
    tab,
    (outcome: CleanupOutcome | null) => {
      success.current = outcome?.tone === "success" ? outcome.text : null;
      setProblem(outcome?.tone === "warning" ? outcome.text : null);
    },
    { keepGone: true },
  );
  const gone = !acting && pair.isError && pairGone(pair.error);

  useEffect(() => {
    if (gone) onGone();
    // `onGone` cambia a ogni disegno della scheda: conta soltanto il momento in cui la coppia sparisce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gone]);

  useEffect(() => {
    if (problem !== null) problemRef.current?.focus();
  }, [problem]);

  async function act<T>(work: () => Promise<T>): Promise<T> {
    setActing(true);
    try {
      return await work();
    } finally {
      setActing(false);
    }
  }

  let body: JSX.Element;
  if (gone) body = <Reason role="status">{t("leadCard.duplicates.gone")}</Reason>;
  else if (pair.data && !(pair.isError && !acting)) {
    const group = pair.data;
    body = (
      <GroupView
        key={`${group.key}:${group.survivor.id}:${group.survivor.version}:${group.absorbed.version}`}
        group={group}
        ctx={ctx}
        timeZone={timeZone}
        busy={busy}
        canChangeOwner={canChangeOwner}
        onMerge={(choices) =>
          void act(async () => {
            const response = await mergeGroup(group, choices);
            if (response) onMerged(response, success.current ?? "");
          })
        }
        onDismiss={() =>
          void act(async () => {
            if (await dismissGroup(group)) onDismissed(success.current ?? "");
          })
        }
      />
    );
  } else if (pair.isError) {
    body = (
      <div className="g-cleanup__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, pair.error)}
        </Reason>
        <Button onClick={() => void pair.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else body = <p role="status">{t("common.loading")}</p>;

  const footer = (
    <>
      {problem !== null ? (
        <p ref={problemRef} tabIndex={-1} className="g-field__error g-cleanup__outcome" role="alert">
          {problem}
        </p>
      ) : null}
      <Button onClick={onClose}>{t("common.close")}</Button>
    </>
  );

  return (
    <Dialog open size="table" title={t("dataCleanup.title")} onClose={onClose} footer={footer}>
      {body}
    </Dialog>
  );
}
